import { QueryClientContext } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { JwtAccessClaimsSchema, type JwtAccessClaimsDto, type TokenPairDto } from '@org/contracts';

import {
  apiClient,
  clearAccessTokenGetter,
  clearOnAccessTokenRefreshed,
  clearOnAuthCleared,
  clearOnTenantSuspended,
  refreshAccessToken,
  setAccessTokenGetter,
  setOnAccessTokenRefreshed,
  setOnAuthCleared,
  setOnTenantSuspended,
} from '@/api/client';
import { grantStore } from '@/auth/tenant-access/grant-store';
import { RouteFallback } from '@/components/RouteFallback';

export type AuthStatus = 'idle' | 'refreshing' | 'authenticated' | 'unauthenticated';

/** Why the app signed the user out by itself. Shown instead of the sign-in page. */
export type SignedOutReason = 'tenant_suspended';

export interface AuthContextValue {
  accessToken: string | null;
  claims: JwtAccessClaimsDto | null;
  /**
   * Multi-tenant tenant UUID surfaced from the JWT `tid` claim.
   *
   * ADR-0004 §"tid overload": the JWT shape is identical across EMR and
   * EMRA but the semantics of `tid` differ — EMR encodes the clinic id
   * (single-tenant per ADR-0004 §1), EMRA encodes the actual tenant UUID.
   * The web layer renames it to `tenantId` at the consumer boundary so
   * downstream code in features/tenants/, features/results/, etc. cannot
   * accidentally treat it as a clinic identifier.
   */
  tenantId: string | null;
  status: AuthStatus;
  refreshing: boolean;
  login: (tokens: TokenPairDto) => void;
  logout: () => Promise<void>;
  /**
   * Drops any earlier session before a new sign-in starts (login, invite
   * accept, signup). Ends it on the server when one is held, then clears the
   * access token, every grant and the query cache. The next user never
   * inherits the earlier user's token or data.
   */
  resetSession: () => Promise<void>;
  /** Set when the app signed the user out by itself, until they move on. */
  signedOutReason: SignedOutReason | null;
  /** The user has read why they were signed out. */
  acknowledgeSignedOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  /**
   * Test-only override — disables the bootstrap silent refresh and starts the
   * provider in `unauthenticated`. Production code does not pass this.
   */
  skipBootstrap?: boolean;
}

export function AuthProvider({ children, skipBootstrap = false }: AuthProviderProps): ReactNode {
  // Access token held in React state ONLY. Per ADR-0005 §9: "Access token in
  // `AuthProvider` React state only — never `localStorage`/`sessionStorage`/
  // `IndexedDB`/cookies." HIPAA (ADR-0003 §0) — XSS-readable storage of a
  // JWT carrying tenant context is incompatible with the baseline posture.
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [claims, setClaims] = useState<JwtAccessClaimsDto | null>(null);
  const [status, setStatus] = useState<AuthStatus>(skipBootstrap ? 'unauthenticated' : 'idle');
  const [signedOutReason, setSignedOutReason] = useState<SignedOutReason | null>(null);
  const suspendedHandled = useRef(false);
  // Optional so the provider still renders in tests without a query client.
  const queryClient = useContext(QueryClientContext);

  // The interceptor needs a stable getter that always reads the latest token.
  // A ref keeps it synchronous (state reads are async via re-render).
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = accessToken;

  const clearAuthState = useCallback((): void => {
    // Grants are worthless without the session, and must not outlive it.
    grantStore.clear();
    // Drop the token for the interceptor now. A request sent before the
    // next render must not pick up the old one.
    tokenRef.current = null;
    setAccessToken(null);
    setClaims(null);
    setStatus('unauthenticated');
  }, []);

  const applyAccessToken = useCallback((nextAccessToken: string): void => {
    const decoded = decodeAccessClaims(nextAccessToken);
    // Update the ref at once, so a request sent before the re-render (such
    // as the interceptor's retry after a refresh) already uses the new token.
    tokenRef.current = nextAccessToken;
    suspendedHandled.current = false;
    setSignedOutReason(null);
    setAccessToken(nextAccessToken);
    setClaims(decoded);
    setStatus('authenticated');
  }, []);

  const applyTokens = useCallback(
    (tokens: TokenPairDto): void => {
      applyAccessToken(tokens.accessToken);
    },
    [applyAccessToken],
  );

  // The user's clinic was suspended (ADR-0023 §8). Show the suspended
  // screen at once, end the session on the server, and clear local state.
  // Many requests can fail together, so this runs once.
  const handleTenantSuspended = useCallback(async (): Promise<void> => {
    if (suspendedHandled.current) {
      return;
    }
    suspendedHandled.current = true;
    setSignedOutReason('tenant_suspended');
    grantStore.clear();
    try {
      // `/v1/auth/**` still works for a suspended clinic.
      await apiClient.post('/v1/auth/logout', {});
    } catch (error) {
      console.warn(
        '[ema-web] logout after suspension failed; clearing local state anyway',
        (error as { response?: { status?: number } }).response?.status ?? 'no-status',
      );
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const acknowledgeSignedOut = useCallback((): void => {
    setSignedOutReason(null);
  }, []);

  // Register/clear the interceptor callbacks. The interceptor must be able to
  // (a) read the current token on every request and (b) clear our state when
  // a refresh fails. Both callbacks are module-level in src/api/client.ts.
  useEffect(() => {
    setAccessTokenGetter(() => tokenRef.current);
    setOnAuthCleared(() => {
      clearAuthState();
    });
    setOnTenantSuspended(() => {
      void handleTenantSuspended();
    });
    // A refresh in the interceptor rotates the access token. Take it, so
    // later requests and the claims follow.
    setOnAccessTokenRefreshed((nextAccessToken) => {
      applyAccessToken(nextAccessToken);
    });
    return () => {
      clearAccessTokenGetter();
      clearOnAuthCleared();
      clearOnTenantSuspended();
      clearOnAccessTokenRefreshed();
    };
  }, [applyAccessToken, clearAuthState, handleTenantSuspended]);

  // Bootstrap silent refresh. It shares the interceptor's in-flight refresh,
  // so when StrictMode runs this effect twice, both runs get the one answer.
  // Two separate calls would send the same refresh cookie twice, and the
  // server would treat the second as reuse and end the session.
  useEffect(() => {
    if (skipBootstrap) {
      return;
    }
    let cancelled = false;
    async function bootstrap(): Promise<void> {
      setStatus('refreshing');
      try {
        const nextAccessToken = await refreshAccessToken();
        if (cancelled) {
          return;
        }
        if (nextAccessToken === null) {
          console.warn('[ema-web] bootstrap refresh returned no access token');
          clearAuthState();
          return;
        }
        applyAccessToken(nextAccessToken);
      } catch (error) {
        if (cancelled) {
          return;
        }
        // PHI-free diagnostic: only status + endpoint, never bodies/tokens.
        console.warn(
          '[ema-web] bootstrap refresh failed',
          (error as { response?: { status?: number } }).response?.status ?? 'no-status',
        );
        clearAuthState();
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [applyAccessToken, clearAuthState, skipBootstrap]);

  const login = useCallback(
    (tokens: TokenPairDto): void => {
      applyTokens(tokens);
    },
    [applyTokens],
  );

  const logout = useCallback(async (): Promise<void> => {
    // End open clinic access on the server first, so it doesn't keep
    // counting against the admin's limit (ADR-0023). Best effort.
    await Promise.allSettled(
      grantStore.getSnapshot().map((grant) => {
        return apiClient.delete(`/v1/tenants/${grant.tenantId}/access-grants/${grant.grantId}`);
      }),
    );
    grantStore.clear();
    try {
      await apiClient.post('/v1/auth/logout', {});
    } catch (error) {
      // Backend may 401 if the token is already expired. Still clear local
      // state — the user clicked logout.
      console.warn(
        '[ema-web] logout call failed; clearing local state anyway',
        (error as { response?: { status?: number } }).response?.status ?? 'no-status',
      );
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const resetSession = useCallback(async (): Promise<void> => {
    if (tokenRef.current !== null) {
      await logout();
    }
    grantStore.clear();
    clearAuthState();
    queryClient?.clear();
  }, [logout, clearAuthState, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      accessToken,
      claims,
      // ADR-0004 §"tid overload": EMRA reads `tid` as the actual tenant UUID
      // (EMR reads it as CLINIC_ID; same JWT shape, different semantic).
      tenantId: claims?.tid ?? null,
      status,
      refreshing: status === 'refreshing' || status === 'idle',
      login,
      logout,
      resetSession,
      signedOutReason,
      acknowledgeSignedOut,
    }),
    [
      accessToken,
      claims,
      status,
      login,
      logout,
      resetSession,
      signedOutReason,
      acknowledgeSignedOut,
    ],
  );

  if (status === 'idle' || status === 'refreshing') {
    return (
      <AuthContext.Provider value={value}>
        <RouteFallback />
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth() must be used inside <AuthProvider>');
  }
  return value;
}

function decodeAccessClaims(accessToken: string): JwtAccessClaimsDto | null {
  try {
    const raw = jwtDecode(accessToken);
    return JwtAccessClaimsSchema.parse(raw);
  } catch (error) {
    console.error(
      '[ema-web] failed to decode access-token claims',
      (error as Error).name ?? 'unknown',
    );
    return null;
  }
}
